import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import AccessDenied from "@/pages/admin/components/AccessDenied";
import ServiceFormModal, {
  type ServiceFormValues,
} from "./components/ServiceFormModal";

interface ServiceRecord {
  id: string;
  name: string;
  slug: string;
  short_name: string;
  description: string;
  price: number;
  promo_price: number;
  duration: string;
  image: string;
  benefits: string[];
  sort_order: number;
}

const API_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-services`;

const formatPrice = (v: number) => v.toLocaleString("vi-VN") + " đ";

export default function AdminServices() {
  const { user, session, isAdmin, loading: authLoading, profileLoading } = useAuth();
  const [services, setServices] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ServiceRecord | null>(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          "Content-Type": "application/json",
        },
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (!res.ok || parsed?.code !== "OK") {
        setError(parsed?.meta?.message || parsed?.message || text || "Lỗi khi tải dữ liệu");
        return;
      }
      setServices(parsed.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi kết nối");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (isAdmin) {
      fetchServices();
    }
  }, [fetchServices, isAdmin]);

  const openAdd = () => {
    setEditing(null);
    setActionError("");
    setModalOpen(true);
  };

  const openEdit = (svc: ServiceRecord) => {
    setEditing(svc);
    setActionError("");
    setModalOpen(true);
  };

  const handleSubmit = async (values: ServiceFormValues) => {
    setSaving(true);
    setActionError("");
    try {
      const payload = {
        name: values.name,
        short_name: values.short_name,
        description: values.description,
        price: Number(values.price),
        promo_price: Number(values.promo_price || 0),
        duration: values.duration,
        image: values.image,
        benefits: values.benefits
          .split("\n")
          .map((b) => b.trim())
          .filter(Boolean),
        sort_order: Number(values.sort_order || 0),
      };

      const res = await fetch(API_URL, {
        method: editing ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editing ? { id: editing.id, ...payload } : payload),
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (!res.ok || parsed?.code !== "OK") {
        setActionError(parsed?.meta?.message || parsed?.message || "Thao tác thất bại");
        return;
      }
      setModalOpen(false);
      await fetchServices();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Lỗi kết nối");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    setError("");
    try {
      const res = await fetch(API_URL, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: confirmDelete.id }),
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (!res.ok || parsed?.code !== "OK") {
        setError(parsed?.meta?.message || parsed?.message || "Xóa thất bại");
        return;
      }
      setServices((prev) => prev.filter((s) => s.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi kết nối");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = services.filter((s) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      s.name.toLowerCase().includes(term) ||
      s.short_name.toLowerCase().includes(term) ||
      s.slug.toLowerCase().includes(term)
    );
  });

  const stats = {
    total: services.length,
    avgPrice: services.length
      ? Math.round(services.reduce((sum, s) => sum + s.price, 0) / services.length)
      : 0,
    hasPromo: services.filter((s) => s.promo_price > 0 && s.promo_price < s.price).length,
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
            <i className="ri-lock-line text-2xl" />
          </div>
          <h1 className="font-display text-2xl font-bold text-foreground-950 mb-2">
            Yêu cầu đăng nhập
          </h1>
          <p className="text-sm text-foreground-600 font-body mb-6">
            Vui lòng đăng nhập để truy cập trang quản lý dịch vụ.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium rounded-md transition-colors cursor-pointer"
          >
            Đăng nhập
          </Link>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Admin header */}
      <header className="bg-white border-b border-background-200/70 sticky top-0 z-30">
        <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-leaf-line text-white text-sm" />
              </div>
              <span className="font-display text-lg font-bold text-foreground-950">
                Xanh Wellness
              </span>
            </Link>
            <span className="hidden sm:inline text-foreground-300">|</span>
            <span className="hidden sm:inline text-sm font-label font-medium text-foreground-600">
              Quản lý dịch vụ
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/admin/bookings"
              className="px-3 py-1.5 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Lịch hẹn
            </Link>
            <span className="hidden sm:inline text-sm text-foreground-600 font-body">
              {user.email}
            </span>
            <button
              onClick={() => supabase.auth.signOut()}
              className="px-3 py-1.5 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 border border-background-300/60 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className="w-full px-4 md:px-8 py-6 md:py-8">
        {/* Stats + add button */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">
              Quản lý dịch vụ
            </h1>
            <p className="text-sm text-foreground-600 font-body mt-1">
              Sửa giá, thêm hoặc xóa dịch vụ hiển thị trên website.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-base" />
            Thêm dịch vụ
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5">
            <p className="text-xs font-label text-foreground-500 mb-1">Tổng dịch vụ</p>
            <p className="font-display text-2xl md:text-3xl font-bold text-foreground-900">
              {stats.total}
            </p>
          </div>
          <div className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5">
            <p className="text-xs font-label text-foreground-500 mb-1">Giá trung bình</p>
            <p className="font-display text-2xl md:text-3xl font-bold text-primary-600">
              {formatPrice(stats.avgPrice)}
            </p>
          </div>
          <div className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5">
            <p className="text-xs font-label text-foreground-500 mb-1">Đang khuyến mãi</p>
            <p className="font-display text-2xl md:text-3xl font-bold text-accent-600">
              {stats.hasPromo}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5 mb-4">
          <div className="relative max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm theo tên dịch vụ..."
              className="w-full pl-9 pr-4 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center justify-between gap-3 bg-accent-100 text-accent-700 rounded-lg px-4 py-3 mb-4">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line" />
              <span className="text-sm font-body">{error}</span>
            </div>
            <button
              onClick={fetchServices}
              className="text-sm font-label font-medium underline cursor-pointer whitespace-nowrap"
            >
              Thử lại
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-background-200/70 rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-20 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-3 inline-block" />
              <p className="text-sm text-foreground-500 font-body">Đang tải dịch vụ...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-price-tag-3-line text-2xl" />
              </div>
              <p className="text-sm text-foreground-500 font-body">
                {searchTerm ? "Không tìm thấy dịch vụ phù hợp" : "Chưa có dịch vụ nào"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-50/50">
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Dịch vụ
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Giá
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Thời lượng
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Lợi ích
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Thứ tự
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-background-100 hover:bg-background-50/60 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {s.image ? (
                            <img
                              src={s.image}
                              alt={s.name}
                              className="w-12 h-12 rounded-md object-cover object-top flex-shrink-0 bg-background-100"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                              <i className="ri-price-tag-3-line text-foreground-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 font-body truncate max-w-[240px]">
                              {s.name}
                            </p>
                            <p className="text-xs text-foreground-500 font-body truncate max-w-[240px]">
                              {s.short_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm font-medium text-foreground-900 font-body">
                          {formatPrice(s.price)}
                        </p>
                        {s.promo_price > 0 && s.promo_price < s.price && (
                          <p className="text-xs text-accent-600 font-body">
                            KM: {formatPrice(s.promo_price)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm text-foreground-700 font-body">{s.duration}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-label font-medium text-foreground-600 bg-background-100 rounded-full px-2.5 py-1">
                          {s.benefits.length} lợi ích
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm text-foreground-700 font-body">{s.sort_order}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openEdit(s)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                            title="Sửa"
                          >
                            <i className="ri-edit-line" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(s)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer"
                            title="Xóa"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-foreground-400 font-body mt-4 text-center">
          Cập nhật lần cuối: {new Date().toLocaleString("vi-VN")}
        </p>
      </main>

      {/* Add/Edit modal */}
      <ServiceFormModal
        open={modalOpen}
        editing={
          editing
            ? {
                name: editing.name,
                short_name: editing.short_name,
                description: editing.description,
                price: String(editing.price),
                promo_price: String(editing.promo_price),
                duration: editing.duration,
                image: editing.image,
                benefits: editing.benefits.join("\n"),
                sort_order: String(editing.sort_order),
              }
            : null
        }
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/40"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-lg p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-delete-bin-line text-xl" />
            </div>
            <h3 className="font-display text-lg font-bold text-foreground-950 mb-2">
              Xóa dịch vụ?
            </h3>
            <p className="text-sm text-foreground-600 font-body mb-6">
              Bạn chắc chắn muốn xóa dịch vụ{" "}
              <strong className="text-foreground-900">{confirmDelete.name}</strong>? Hành động
              này không thể hoàn tác.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 border border-background-300/60 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                disabled={deletingId === confirmDelete.id}
                className="px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-label font-medium rounded-md transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
              >
                {deletingId === confirmDelete.id && (
                  <i className="ri-loader-4-line animate-spin" />
                )}
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}